<template>
  <div class="space-y-4">
    <h2>{{ t('cleaningFees') }}</h2>

    <div class="space-y-4 rounded-md bg-white pb-4 pl-4 pr-4 pt-1">
      <div v-if="!fields.length" class="flex justify-center p-4">
        {{ t('noEntries') }}
      </div>

      <div
        v-for="(_, i) in fields"
        :key="i"
        class="relative border-b pb-3 pt-3 last:border-b-0 last:pb-0"
      >
        <div class="flex items-end gap-4">
          <div class="grid flex-1 grid-cols-4 gap-4">
            <InputField :name="`cleaningFees[${i}].name.de`" :label="t('nameDe')" type="text" />
            <InputField :name="`cleaningFees[${i}].name.en`" :label="t('nameEn')" type="text" />
            <InputField
              :name="`cleaningFees[${i}].price`"
              :label="t('price')"
              type="number"
              :min="0"
              :max-fraction-digits="2"
              adornment="€"
            />
            <div class="pt-[2.125rem]">
              <CheckboxField :name="`cleaningFees[${i}].photoRequired`">{{
                t('photoRequired')
              }}</CheckboxField>
            </div>
          </div>

          <button
            class="px-2 py-3"
            :class="{
              'text-bittersweet-600': fields.length > 1,
              'text-obsidian-400': fields.length === 1,
            }"
            :disabled="fields.length === 1"
            @click.prevent="removeItem(i)"
          >
            <XIcon class="size-4" />
          </button>
        </div>

        <div class="mt-3 grid grid-cols-2 gap-4">
          <TextareaField :name="`cleaningFees[${i}].guidelines.de`" :label="t('guidelinesDe')" />
          <TextareaField :name="`cleaningFees[${i}].guidelines.en`" :label="t('guidelinesEn')" />
        </div>
      </div>

      <InputHint :hint="error" is-error />

      <div class="flex justify-center">
        <CVButton variant="ghost" @click.prevent="addItem">{{ t('addCleaningFee') }}</CVButton>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import { v4 as uuidv4 } from 'uuid';
import { useFieldArray, useFieldError } from 'vee-validate';
import InputField from '@/components/InputField.vue';
import CheckboxField from '@/components/CheckboxField.vue';
import TextareaField from '@/components/TextareaField.vue';
import InputHint from '@/components/InputHint.vue';
import { XIcon } from 'lucide-vue-next';
import { Alert } from '@/services/alert.service';

const { t } = useI18n();

const { fields, push, remove } = useFieldArray('cleaningFees');
const error = useFieldError('cleaningFees');

const addItem = () => {
  push({
    id: uuidv4(),
    name: { de: '', en: '' },
    price: 0,
    photoRequired: false,
    guidelines: { de: '', en: '' },
  });
};

const removeItem = async (i: number) => {
  const { isDenied } = await Alert.fire({
    text: t('reallyDeleteMessage'),
    icon: 'warning',
    showConfirmButton: false,
    showDenyButton: true,
    showCancelButton: true,
    denyButtonText: t('delete'),
    cancelButtonText: t('cancel'),
  });
  if (!isDenied) {
    return;
  }
  remove(i);
};
</script>

<i18n lang="json">
{
  "en": {
    "cleaningFees": "Cleaning fees",
    "addCleaningFee": "+ Add cleaning fee",
    "nameDe": "Name (German)",
    "nameEn": "Name (English)",
    "price": "Price",
    "photoRequired": "Photo required",
    "guidelinesDe": "Guidelines (German)",
    "guidelinesEn": "Guidelines (English)",
    "noEntries": "No Entries",
    "reallyDeleteMessage": "Do you really want to delete this item?",
    "delete": "Delete",
    "cancel": "Cancel"
  },
  "de": {
    "cleaningFees": "Reinigungsgebühren",
    "addCleaningFee": "+ Reinigungsgebühr hinzufügen",
    "nameDe": "Name (Deutsch)",
    "nameEn": "Name (Englisch)",
    "price": "Preis",
    "photoRequired": "Foto erforderlich",
    "guidelinesDe": "Richtlinien (Deutsch)",
    "guidelinesEn": "Richtlinien (Englisch)",
    "noEntries": "Keine Einträge",
    "reallyDeleteMessage": "Möchtest du diesen Eintrag wirklich löschen?",
    "delete": "Löschen",
    "cancel": "Abbrechen"
  },
  "es": {
    "cleaningFees": "Tasas de limpieza",
    "addCleaningFee": "+ Añadir tasa de limpieza",
    "nameDe": "Nombre (alemán)",
    "nameEn": "Nombre (inglés)",
    "price": "Precio",
    "photoRequired": "Foto obligatoria",
    "guidelinesDe": "Directrices (alemán)",
    "guidelinesEn": "Directrices (inglés)",
    "noEntries": "Sin entradas",
    "reallyDeleteMessage": "¿Seguro que quieres eliminar esta entrada?",
    "delete": "Eliminar",
    "cancel": "Cancelar"
  }
}
</i18n>
